/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "../pages/HustleEatsPage.css";
import "../components/phase2/phase2.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* Diner-side smart-menu + split-bill page.
   Diner scans table QR → picks seat → orders from interactive menu →
   declares as Payer / scans Payer's QR to be covered. */

// Demo "menu" — real venues would feed this from their POS or a
// dedicated menu module. Phase 2 just needs to prove the order +
// payer flow works.
const DEMO_MENU = [
  { name: "Cacio e pepe", price_cents: 2800, category: "food" },
  { name: "Margherita", price_cents: 2400, category: "food" },
  { name: "Gnocchi al ragu", price_cents: 3000, category: "food" },
  { name: "Tiramisu", price_cents: 1400, category: "dessert" },
  { name: "Chianti (glass)", price_cents: 1600, category: "alcohol" },
  { name: "Aperol Spritz", price_cents: 1800, category: "alcohol" },
];

export default function TableSessionPage() {
  const { qr_token } = useParams();
  const [authNeeded, setAuthNeeded] = useState(false);
  const [seat, setSeat] = useState(1);
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ owed_cents: 0, covered_by_others_cents: 0, total_cents: 0 });
  const [payerInfo, setPayerInfo] = useState(null);
  const [payerScanInput, setPayerScanInput] = useState("");
  const [payerRequests, setPayerRequests] = useState([]);
  const [msg, setMsg] = useState(null);
  const inflightRef = useRef(false);

  const openTab = async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const { data } = await axios.post(
        `${API}/api/hustleeats/dining/sessions/open`,
        { qr_token, seat_number: Number(seat) },
        { withCredentials: true },
      );
      setSession(data);
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else setMsg({ kind: "err", text: e?.response?.data?.detail || "Couldn't open your tab." });
    } finally {
      inflightRef.current = false;
    }
  };

  const refresh = async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(
        `${API}/api/hustleeats/dining/sessions/${session.id}`,
        { withCredentials: true },
      );
      setItems(data.items || []);
      setTotals({
        owed_cents: data.owed_cents,
        covered_by_others_cents: data.covered_by_others_cents,
        total_cents: data.total_cents,
      });
      if (session.is_payer) {
        const { data: reqs } = await axios.get(
          `${API}/api/hustleeats/dining/sessions/${session.id}/payer-requests`,
          { withCredentials: true },
        );
        setPayerRequests(reqs);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    if (!session) return;
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [session?.id, session?.is_payer]);

  const addItem = async (item) => {
    if (!session) return;
    try {
      await axios.post(
        `${API}/api/hustleeats/dining/sessions/${session.id}/items`,
        item, { withCredentials: true },
      );
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Couldn't add item." });
    }
  };

  const becomePayer = async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/hustleeats/dining/sessions/${session.id}/become-payer`,
        {}, { withCredentials: true },
      );
      setPayerInfo(data);
      setSession({ ...session, is_payer: true });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Couldn't become Payer." });
    }
  };

  const scanPayer = async () => {
    if (!payerScanInput) return;
    try {
      // Accept either raw token or full "hustlepayer:TOKEN" payload
      const token = payerScanInput.startsWith("hustlepayer:")
        ? payerScanInput.slice("hustlepayer:".length)
        : payerScanInput;
      const { data } = await axios.post(
        `${API}/api/hustleeats/dining/sessions/${session.id}/link-payer`,
        { payer_token: token }, { withCredentials: true },
      );
      setMsg({ kind: "ok", text: `Link requested. Payer at seat ${data.payer_seat_number} must approve.` });
      setPayerScanInput("");
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Scan failed." });
    }
  };

  const actOnLink = async (linkId, approve) => {
    await axios.post(
      `${API}/api/hustleeats/dining/payer-links/act`,
      { link_id: linkId, approve },
      { withCredentials: true },
    );
    refresh();
  };

  if (authNeeded) {
    return (
      <div className="te-page">
        <div className="te-hero"><div className="te-hero__inner">
          <h1 className="he-h1">Sign in to open your tab</h1>
          <p className="he-lede">Your seat preference, order history, and HustleEats wallet are tied to your account.</p>
        </div></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="te-page">
        <div className="te-hero"><div className="te-hero__inner">
          <span className="he-eyebrow">HustleEats · Table</span>
          <h1 className="he-h1">What seat are you at?</h1>
          <p className="he-lede">Pick your seat and we'll open your tab. You can order, split the bill, and pay from your phone.</p>
          <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
            <input
              type="number"
              min={1}
              max={30}
              value={seat}
              onChange={(e) => setSeat(e.target.value)}
              style={{ width: 80, padding: 10, fontSize: 16, borderRadius: 6, border: "1px solid var(--he-line)" }}
              data-testid="te-seat-input"
            />
            <button className="he-card__cta" onClick={openTab} data-testid="te-open-btn">Open my tab</button>
          </div>
        </div></div>
        {msg && <div className="he-banner">{msg.text}</div>}
      </div>
    );
  }

  return (
    <div className="te-page">
      <div className="te-hero"><div className="te-hero__inner">
        <span className="he-eyebrow">Table {session.table_number} · Seat {session.seat_number}</span>
        <h1 className="he-h1">Your tab is open.</h1>
      </div></div>

      {!session.is_payer && (
        <div className="te-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <strong>Paying for the whole table?</strong>
            <div className="te-row__sub">Become a Payer — others can scan your QR to put their orders on your tab.</div>
          </div>
          <button className="ph2-btn" onClick={becomePayer} data-testid="te-become-payer-btn">Become Payer</button>
        </div>
      )}

      {payerInfo && (
        <div className="te-payer-banner" data-testid="te-payer-qr">
          <strong>You're a Payer. Show this to your guests:</strong>
          <code>{payerInfo.qr_payload}</code>
        </div>
      )}

      {session.is_payer && payerRequests.length > 0 && (
        <div className="te-card" data-testid="te-payer-requests">
          <h3 className="ph2-h3">Guest link requests</h3>
          {payerRequests.map((req) => (
            <div className="te-row" key={req.link_id}>
              <div>
                <div className="te-row__name">Seat {req.guest_seat_number} wants you to cover them</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="ph2-btn" onClick={() => actOnLink(req.link_id, true)} data-testid={`te-link-approve-${req.link_id}`}>Approve</button>
                <button className="ph2-btn ph2-btn--ghost" onClick={() => actOnLink(req.link_id, false)}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!session.is_payer && (
        <div className="te-card">
          <h3 className="ph2-h3">Or be covered by someone at this table</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              value={payerScanInput}
              onChange={(e) => setPayerScanInput(e.target.value)}
              placeholder="Paste Payer's QR payload (hustlepayer:…)"
              style={{ flex: 1, padding: 10, fontSize: 14, borderRadius: 6, border: "1px solid var(--he-line)" }}
              data-testid="te-payer-scan-input"
            />
            <button className="ph2-btn" onClick={scanPayer} data-testid="te-payer-scan-btn">Link to Payer</button>
          </div>
        </div>
      )}

      <div className="te-card">
        <h3 className="ph2-h3">Menu</h3>
        {DEMO_MENU.map((item) => (
          <div className="te-row" key={item.name}>
            <div>
              <div className="te-row__name">{item.name}</div>
              <div className="te-row__sub">{item.category}</div>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span className="te-row__price">${(item.price_cents / 100).toFixed(2)}</span>
              <button className="ph2-btn" onClick={() => addItem(item)} data-testid={`te-add-${item.name.replace(/\s+/g, "-")}`}>Add</button>
            </div>
          </div>
        ))}
      </div>

      <div className="te-card">
        <h3 className="ph2-h3">Your tab ({items.length} items)</h3>
        {items.length === 0 && <div className="he-state">Nothing yet — add from the menu above.</div>}
        {items.map((i) => (
          <div className="te-row" key={i.id} data-testid={`te-item-${i.id}`}>
            <div>
              <div className="te-row__name">{i.name}</div>
              <div className="te-row__sub">{i.category}{i.payer_session_id ? " · covered by another diner" : ""}</div>
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <span className="te-row__price">${(i.price_cents / 100).toFixed(2)}</span>
              <span className={`te-row__status te-row__status--${i.status === "pending_approval" ? "pending" : i.status}`}>{i.status}</span>
            </div>
          </div>
        ))}
        <div className="te-totals">
          <div><span>You owe</span><strong>${(totals.owed_cents / 100).toFixed(2)}</strong></div>
          <div><span>Others covering</span><strong>${(totals.covered_by_others_cents / 100).toFixed(2)}</strong></div>
          <div><span>Tab total</span><strong>${(totals.total_cents / 100).toFixed(2)}</strong></div>
        </div>
      </div>

      {msg && <div className="he-banner">{msg.text}</div>}
    </div>
  );
}
